import React from 'react';
import PropTypes from 'prop-types';
import { groupWorkoutsByTimeOfDay, getTimeOfDayLabel } from '../utils/workoutFormatters';
import { getTriClubEventsByTimeSlot } from '../utils/triClubUtils';
import DayTimeSlot from './DayTimeSlot';
import '../styles/CalendarDay.css';

/**
 * CalendarDay component represents a single day cell in the calendar
 *
 * @param {Object} dayObj - Day object with date, day, year, month, isCurrentMonth
 * @param {Array} workouts - Array of workouts for this day
 * @param {Object} triClubSchedule - Tri club schedule with weekly events
 * @param {boolean} isToday - Whether this day is today
 * @param {boolean} showTimeSlots - Whether to show time slots (used for drag-drop state)
 * @param {Object} dragState - Drag state object { draggedWorkout, dragOverDate, dragOverTimeSlot }
 * @param {Function} onDragOver - Drag over handler
 * @param {Function} onDragLeave - Drag leave handler
 * @param {Function} onDrop - Drop handler
 * @param {Function} onWorkoutClick - Workout click handler
 * @param {Function} onWorkoutSelectionToggle - Workout selection toggle handler
 * @param {Function} onWorkoutDragStart - Workout drag start handler
 * @param {Function} onWorkoutDragEnd - Workout drag end handler
 */
function CalendarDay({
  dayObj,
  workouts,
  triClubSchedule,
  isToday,
  showTimeSlots,
  dragState,
  onDragOver,
  onDragLeave,
  onDrop,
  onWorkoutClick,
  onWorkoutSelectionToggle,
  onWorkoutHeatTrainingChange,
  onWorkoutDragStart,
  onWorkoutDragEnd,
  onAddWorkout,
  locationName
}) {
  const { draggedWorkout, dragOverDate, dragOverTimeSlot } = dragState;

  const workoutGroups = groupWorkoutsByTimeOfDay(workouts);
  const triClubEventsBySlot = getTriClubEventsByTimeSlot(dayObj.date, triClubSchedule);

  // Stable date string used as key prefix so DayTimeSlot remounts with fresh state
  // when the calendar navigates to a different week.
  const dateStr = dayObj.date.toISOString().split('T')[0];

  return (
    <div
      className={`calendar-day ${isToday ? 'is-today' : ''} time-slot-mode`}
    >
      <div className="day-number">{dayObj.day}</div>
      {locationName && <div className="location-indicator">{locationName}</div>}
      <div className="time-slots-container">
        <DayTimeSlot
          key={`${dateStr}-morning`}
          dayObj={dayObj}
          timeSlot="morning"
          workouts={workoutGroups.morning}
          triClubEvents={triClubEventsBySlot.morning}
          draggedWorkout={draggedWorkout}
          dragOverDate={dragOverDate}
          dragOverTimeSlot={dragOverTimeSlot}
          onDragOver={(e) => onDragOver(e, dayObj.date, 'morning')}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, dayObj, 'morning')}
          onWorkoutDragStart={onWorkoutDragStart}
          onWorkoutDragEnd={onWorkoutDragEnd}
          onWorkoutClick={onWorkoutClick}
          onWorkoutSelectionToggle={onWorkoutSelectionToggle}
          onWorkoutHeatTrainingChange={onWorkoutHeatTrainingChange}
          onAddWorkout={onAddWorkout}
          getTimeOfDayLabel={getTimeOfDayLabel}
        />
        <DayTimeSlot
          key={`${dateStr}-afternoon`}
          dayObj={dayObj}
          timeSlot="afternoon"
          workouts={workoutGroups.afternoon}
          triClubEvents={triClubEventsBySlot.afternoon}
          draggedWorkout={draggedWorkout}
          dragOverDate={dragOverDate}
          dragOverTimeSlot={dragOverTimeSlot}
          onDragOver={(e) => onDragOver(e, dayObj.date, 'afternoon')}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, dayObj, 'afternoon')}
          onWorkoutDragStart={onWorkoutDragStart}
          onWorkoutDragEnd={onWorkoutDragEnd}
          onWorkoutClick={onWorkoutClick}
          onWorkoutSelectionToggle={onWorkoutSelectionToggle}
          onWorkoutHeatTrainingChange={onWorkoutHeatTrainingChange}
          onAddWorkout={onAddWorkout}
          getTimeOfDayLabel={getTimeOfDayLabel}
        />
        <DayTimeSlot
          key={`${dateStr}-evening`}
          dayObj={dayObj}
          timeSlot="evening"
          workouts={workoutGroups.evening}
          triClubEvents={triClubEventsBySlot.evening}
          draggedWorkout={draggedWorkout}
          dragOverDate={dragOverDate}
          dragOverTimeSlot={dragOverTimeSlot}
          onDragOver={(e) => onDragOver(e, dayObj.date, 'evening')}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, dayObj, 'evening')}
          onWorkoutDragStart={onWorkoutDragStart}
          onWorkoutDragEnd={onWorkoutDragEnd}
          onWorkoutClick={onWorkoutClick}
          onWorkoutSelectionToggle={onWorkoutSelectionToggle}
          onWorkoutHeatTrainingChange={onWorkoutHeatTrainingChange}
          onAddWorkout={onAddWorkout}
          getTimeOfDayLabel={getTimeOfDayLabel}
        />
        {workoutGroups.unscheduled.length > 0 && (
          <DayTimeSlot
            key={`${dateStr}-unscheduled`}
            dayObj={dayObj}
            timeSlot="unscheduled"
            workouts={workoutGroups.unscheduled}
            triClubEvents={[]}
            draggedWorkout={draggedWorkout}
            dragOverDate={dragOverDate}
            dragOverTimeSlot={dragOverTimeSlot}
            onDragOver={(e) => onDragOver(e, dayObj.date, 'unscheduled')}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, dayObj, 'unscheduled')}
            onWorkoutDragStart={onWorkoutDragStart}
            onWorkoutDragEnd={onWorkoutDragEnd}
            onWorkoutClick={onWorkoutClick}
            onWorkoutSelectionToggle={onWorkoutSelectionToggle}
            onWorkoutHeatTrainingChange={onWorkoutHeatTrainingChange}
            getTimeOfDayLabel={getTimeOfDayLabel}
          />
        )}
      </div>
    </div>
  );
}

CalendarDay.propTypes = {
  dayObj: PropTypes.shape({
    day: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
    date: PropTypes.object.isRequired,
    isCurrentMonth: PropTypes.bool,
  }).isRequired,
  workouts: PropTypes.array.isRequired,
  triClubSchedule: PropTypes.shape({
    effective_date: PropTypes.string,
    schedule: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          time: PropTypes.string.isRequired,
          activity: PropTypes.string.isRequired,
        })
      )
    ),
  }),
  isToday: PropTypes.bool.isRequired,
  showTimeSlots: PropTypes.bool,
  dragState: PropTypes.shape({
    draggedWorkout: PropTypes.object,
    dragOverDate: PropTypes.string,
    dragOverTimeSlot: PropTypes.string,
  }).isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onWorkoutClick: PropTypes.func.isRequired,
  onWorkoutSelectionToggle: PropTypes.func,
  onWorkoutHeatTrainingChange: PropTypes.func,
  onWorkoutDragStart: PropTypes.func.isRequired,
  onWorkoutDragEnd: PropTypes.func.isRequired,
  onAddWorkout: PropTypes.func,
  locationName: PropTypes.string,
};

CalendarDay.defaultProps = {
  showTimeSlots: false,
  triClubSchedule: null,
  onWorkoutHeatTrainingChange: null,
  onAddWorkout: null,
  locationName: null,
};

export default CalendarDay;
